import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import type { CriticalDependency } from '@bliks-io/contracts';
import {
  Alert,
  AlertDescription,
  Badge,
  getColorSchemeFromString,
  Button,
  UserCard,
  DataTable,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';

interface CriticalDependenciesCardProps {
  dependencies: CriticalDependency[];
}

const INITIAL_DISPLAY_COUNT = 5;

export function CriticalDependenciesCard({ dependencies }: CriticalDependenciesCardProps) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);

  // Column definitions for critical dependencies table (must be before early return)
  const columns: DataTableColumnDef<CriticalDependency>[] = useMemo(
    () => [
      {
        id: 'processStep',
        header: createColumnHeader(t('organization.systems.criticalDependencies.processStep')),
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.process.name}</div>
            <div className="text-sm text-muted-foreground">{row.original.stepName}</div>
          </div>
        )
      },
      {
        id: 'assignedTo',
        header: createColumnHeader(t('organization.systems.criticalDependencies.assignedTo')),
        cell: ({ row }) => (
          <UserCard
            user={{
              firstName: row.original.assignedTo.firstName,
              lastName: row.original.assignedTo.lastName,
              email: row.original.assignedTo.email,
              avatar: row.original.assignedTo.avatar
            }}
            variant="name-only"
            size="sm"
          />
        )
      },
      {
        id: 'role',
        header: createColumnHeader(t('organization.systems.criticalDependencies.role')),
        cell: ({ row }) =>
          row.original.role ? (
            <Badge shape="rounded" colorScheme={getColorSchemeFromString(row.original.role._id)}>
              {row.original.role.name}
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
        meta: { fitContent: true }
      }
    ],
    [t]
  );

  // Don't render if no critical dependencies
  if (dependencies.length === 0) {
    return null;
  }

  const displayedDependencies = showAll
    ? dependencies
    : dependencies.slice(0, INITIAL_DISPLAY_COUNT);

  const hasMore = dependencies.length > INITIAL_DISPLAY_COUNT;

  return (
    <Alert className="border-l-4 border-l-warning-solid bg-warning-surface">
      <AlertTriangle className="h-5 w-5 text-warning" />
      <AlertDescription className="w-full text-sm">
        <div className="font-medium text-warning-strong mb-1">
          {t('organization.systems.criticalDependencies.title')}
        </div>
        <div className="text-warning-strong mb-4">
          {t('organization.systems.criticalDependencies.description')}
        </div>

        <DataTable
          columns={columns}
          data={displayedDependencies}
          enablePagination={false}
          enableSorting={false}
        />

        {hasMore && (
          <div className="mt-3 flex justify-center">
            <Button
              variant="ghost"
              size="small"
              onClick={() => setShowAll(!showAll)}
              className="text-warning hover:text-warning-strong"
            >
              {showAll ? (
                <>
                  {t('organization.systems.criticalDependencies.showLess')}
                  <ChevronUp className="ml-1 h-4 w-4" />
                </>
              ) : (
                <>
                  {t('organization.systems.criticalDependencies.showAll', {
                    count: dependencies.length
                  })}
                  <ChevronDown className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
